import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  NoteAdd as NoteIcon,
  TaskAlt as DoneIcon,
  Event as EventIcon,
  Translate as TranslateIcon,
  OpenInNew as OpenIcon,
  ContentCopy as CopyIcon,
  Sms as SmsIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { staffAiApi, staffAiError, StaffAiPatientOverview, StaffAiSendResult, StaffAiTaskBrief } from '../../services/staffAiApi';

export interface ProposedAction {
  type: 'note' | 'done' | 'followup';
  text?: string;
  date?: string;
  raw: string;
}

export interface ParsedAnswer {
  body: string;
  draft: string | null;
  scheduling: string | null;
  actions: ProposedAction[];
}

function parseAction(s: string): ProposedAction | null {
  const t = s.trim();
  let m = t.match(/^add (?:a )?note\s*:\s*([\s\S]+)$/i);
  if (m) return { type: 'note', text: m[1].trim(), raw: t };
  if (/^mark (?:the )?task (?:as )?(?:done|completed?)\.?$/i.test(t)) return { type: 'done', raw: t };
  m = t.match(/^follow[- ]?up on (\d{4}-\d{2}-\d{2})\s*(?::\s*([\s\S]*))?$/i);
  if (m) return { type: 'followup', date: m[1], text: (m[2] || '').trim(), raw: t };
  return null;
}

/** Splits an AI answer into the answer itself, the patient-facing draft, a scheduling suggestion and proposed task actions. */
export function parseAnswer(content: string): ParsedAnswer {
  const body: string[] = [];
  const draft: string[] = [];
  const actions: ProposedAction[] = [];
  let scheduling: string | null = null;
  let inDraft = false;
  for (const line of String(content || '').split('\n')) {
    const d = line.match(/^\s*draft message\s*:\s*(.*)$/i);
    if (d) {
      inDraft = true;
      if (d[1].trim()) draft.push(d[1]);
      continue;
    }
    const s = line.match(/^\s*scheduling suggestion\s*:\s*(.*)$/i);
    if (s) {
      inDraft = false;
      scheduling = s[1].trim().replace(/[.\s]+$/, '') || null;
      continue;
    }
    const a = line.match(/^\s*proposed action\s*:\s*(.*)$/i);
    if (a) {
      inDraft = false;
      const p = parseAction(a[1]);
      if (p) actions.push(p);
      else body.push(line);
      continue;
    }
    (inDraft ? draft : body).push(line);
  }
  return { body: body.join('\n').trim(), draft: draft.join('\n').trim() || null, scheduling, actions };
}

export const ago = (iso?: string | null): string => {
  if (!iso) return '';
  const h = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (!Number.isFinite(h) || h < 0) return '';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

const taskLabel = (t: StaffAiTaskBrief, open: boolean) =>
  `${open ? 'Open task' : 'Earlier task'}: ${t.category || 'general'}${t.summary[0] ? ` · ${t.summary[0]}` : t.lastPatientText ? ` · ${t.lastPatientText.slice(0, 60)}` : ''}${t.needsReply ? ' (needs reply)' : ''}`;

/** The patient's current open task, shown above the conversation. */
export const OpenTaskBanner: React.FC<{
  overview: StaffAiPatientOverview | null;
  currentTaskId?: string | null;
  onOpenTask: (id: string) => void;
  onCompose: () => void;
}> = ({ overview, currentTaskId, onOpenTask, onCompose }) => {
  if (!overview) return null;
  const t = overview.openTasks.find((x) => x.id === currentTaskId) || overview.openTasks[0];
  const more = overview.openTasks.length - 1;
  return (
    <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: t?.needsReply ? '#fff8e1' : '#f5f9ff', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {t ? (
        <>
          <Chip size="small" color={t.needsReply ? 'warning' : 'default'} label={t.needsReply ? `Needs your reply · ${ago(t.lastPatientAt)}` : 'Waiting on patient'} />
          <Chip size="small" variant="outlined" label={[t.category, t.priority].filter(Boolean).join(' · ') || 'task'} />
          <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }} noWrap title={t.summary.join('; ') || t.lastPatientText}>
            {t.summary[0] || t.lastPatientText || 'Open triage task'}
            {t.assignedTo ? ` · assigned to ${t.assignedTo}` : ''}
            {t.followUpDate ? ` · follow-up ${String(t.followUpDate).slice(0, 10)}` : ''}
            {more > 0 ? ` · ${more} more open` : ''}
          </Typography>
          <Button size="small" startIcon={<OpenIcon fontSize="small" />} onClick={() => onOpenTask(t.id)}>Open task</Button>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>No open triage task. A text you send from here starts a new one.</Typography>
      )}
      {overview.patient.archived && <Chip size="small" color="default" label="Archived patient" />}
      <Tooltip title={overview.canText ? 'Write a text to this patient. You review it and press Send; it goes through Spruce like a reply from the task.' : overview.noTextReason || ''}>
        <span>
          <Button size="small" variant="outlined" startIcon={<SmsIcon fontSize="small" />} onClick={onCompose} disabled={!overview.canText}>Text the patient</Button>
        </span>
      </Tooltip>
    </Box>
  );
};

/** Review, edit, translate and send a text to the patient. Nothing is sent until Send is pressed. */
export const SendToPatientDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  overview: StaffAiPatientOverview | null;
  threadId: string | null;
  initialText: string;
  aiDraft?: string;
  defaultTaskId?: string | null;
  languages: string[];
  onSent: (r: StaffAiSendResult) => void;
}> = ({ open, onClose, overview, threadId, initialText, aiDraft, defaultTaskId, languages, onSent }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState(initialText);
  const [taskId, setTaskId] = useState('');
  const [language, setLanguage] = useState('');
  const [english, setEnglish] = useState<string | null>(null);
  const [busy, setBusy] = useState<'' | 'translate' | 'send'>('');
  const [error, setError] = useState('');

  const choices = useMemo(() => {
    if (!overview) return [] as { value: string; label: string }[];
    return [
      ...overview.openTasks.filter((t) => t.canText).map((t) => ({ value: t.id, label: taskLabel(t, true) })),
      ...overview.recentTasks.map((t) => ({ value: t.id, label: taskLabel(t, false) })),
      { value: 'new', label: 'Start a new task' },
    ];
  }, [overview]);

  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setEnglish(null);
    setLanguage('');
    setError('');
    const textable = overview?.openTasks.filter((t) => t.canText) || [];
    const first = textable.find((t) => t.id === defaultTaskId) || textable[0];
    setTaskId(first ? first.id : 'new');
  }, [open, initialText, defaultTaskId, overview]);

  const translate = async () => {
    if (!language || !text.trim()) return;
    setBusy('translate');
    setError('');
    try {
      const source = english ?? text;
      const r = await staffAiApi.translate(source, language);
      setEnglish(source);
      setText(r.text);
    } catch (e) {
      setError(staffAiError(e).message);
    } finally {
      setBusy('');
    }
  };

  const send = async () => {
    if (!overview || !text.trim()) return;
    setBusy('send');
    setError('');
    try {
      const r = await staffAiApi.sendToPatient(overview.patient.lmdId, {
        text: text.trim(),
        taskId: taskId || undefined,
        threadId,
        aiDraft: aiDraft || undefined,
        originalText: english || undefined,
      });
      enqueueSnackbar(
        `Sent to ${overview.patient.name}${r.newTask ? ' (new triage task)' : ''}.${r.learning ? ' Your edit is waiting in AI Rating as an example reply.' : ''}`,
        { variant: 'success' },
      );
      onSent(r);
    } catch (e) {
      setError(staffAiError(e).message);
    } finally {
      setBusy('');
    }
  };

  const name = overview?.patient.name || 'the patient';
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Text {name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          Goes to {overview?.patient.phoneLast4 ? `the number ending ${overview.patient.phoneLast4}` : 'the patient'} through Spruce, like a reply from the triage task.
          Nothing is sent until you press Send.
        </Typography>
        {overview && !overview.canText && <Alert severity="error">{overview.noTextReason}</Alert>}
        {overview?.patient.archived && <Alert severity="warning">This patient is archived. Check that this message should still go to them.</Alert>}
        <TextField select size="small" label="Put it on" value={taskId} onChange={(e) => setTaskId(e.target.value)} disabled={!!busy}>
          {choices.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <TextField
          label="Message"
          multiline
          minRows={4}
          maxRows={14}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={!!busy}
          inputProps={{ maxLength: 1600 }}
          helperText={`${text.length.toLocaleString()} / 1,600 characters${aiDraft && text.trim() !== aiDraft.trim() && !english ? ' · edited from the AI draft' : ''}`}
        />
        {english !== null && (
          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">English original (kept in the conversation):</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{english}</Typography>
          </Paper>
        )}
        {languages.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label="Translate to" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!!busy} sx={{ minWidth: 200 }}>
              {languages.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
            <Button size="small" startIcon={<TranslateIcon />} onClick={translate} disabled={!!busy || !language || !text.trim()}>Translate</Button>
            {english !== null && (
              <Button size="small" startIcon={<UndoIcon />} onClick={() => { setText(english); setEnglish(null); }} disabled={!!busy}>Back to English</Button>
            )}
          </Box>
        )}
        {busy && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!busy}>Cancel</Button>
        <Button variant="contained" endIcon={<SendIcon />} onClick={send} disabled={!!busy || !text.trim() || !overview?.canText}>
          {busy === 'send' ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The patient-facing draft inside an AI answer, with Review and send. */
export const DraftBox: React.FC<{ draft: string; canSend: boolean; onReview: () => void }> = ({ draft, canSend, onReview }) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Box sx={{ mt: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flex: 1 }}>Draft message to the patient</Typography>
        <Tooltip title="Copy the draft">
          <Button size="small" startIcon={<CopyIcon fontSize="small" />} onClick={() => { navigator.clipboard?.writeText(draft); enqueueSnackbar('Draft copied.', { variant: 'info' }); }}>Copy</Button>
        </Tooltip>
        <Tooltip title={canSend ? 'Opens the send box: edit or translate it, pick the task, then press Send.' : 'This patient cannot be texted from here.'}>
          <span>
            <Button size="small" variant="contained" startIcon={<SendIcon fontSize="small" />} onClick={onReview} disabled={!canSend}>Review and send</Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{draft}</Typography>
    </Box>
  );
};

const ACTION_TEXT: Record<ProposedAction['type'], string> = { note: 'Add note', done: 'Mark task done', followup: 'Set follow-up' };

/** Task changes the AI proposed. Each runs only after a confirm click. */
export const ActionButtons: React.FC<{
  actions: ProposedAction[];
  scheduling: string | null;
  lmdId: string;
  taskId: string | null;
  threadId: string | null;
  onDone: () => void;
  onBook: (text: string) => void;
}> = ({ actions, scheduling, lmdId, taskId, threadId, onDone, onBook }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<{ i: number; a: ProposedAction } | null>(null);
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Record<number, boolean>>({});
  if (!actions.length && !scheduling) return null;

  const start = (i: number, a: ProposedAction) => {
    setPending({ i, a });
    setText(a.text || '');
    setDate(a.date || '');
  };
  const confirm = async () => {
    if (!pending || !taskId) return;
    setBusy(true);
    try {
      await staffAiApi.taskAction(lmdId, taskId, { type: pending.a.type, text: text.trim() || undefined, date: date || undefined, threadId });
      setDone((d) => ({ ...d, [pending.i]: true }));
      enqueueSnackbar(`${ACTION_TEXT[pending.a.type]}: done on the triage task.`, { variant: 'success' });
      setPending(null);
      onDone();
    } catch (e) {
      enqueueSnackbar(staffAiError(e).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const icon = (t: ProposedAction['type']) => (t === 'note' ? <NoteIcon fontSize="small" /> : t === 'done' ? <DoneIcon fontSize="small" /> : <EventIcon fontSize="small" />);
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      {actions.map((a, i) => (
        <Tooltip key={i} title={taskId ? (done[i] ? 'Done' : 'Proposed by Levitan AI. Nothing changes until you confirm.') : 'This patient has no open task to change.'}>
          <span>
            <Button size="small" variant="outlined" startIcon={icon(a.type)} disabled={!taskId || done[i]} onClick={() => start(i, a)}>
              {done[i] ? `${ACTION_TEXT[a.type]} ✓` : a.type === 'followup' ? `Follow up ${a.date}` : ACTION_TEXT[a.type]}
            </Button>
          </span>
        </Tooltip>
      ))}
      {scheduling && (
        <Tooltip title="Opens the appointment form for this patient, filled in from the suggestion. You pick the time and save it.">
          <Button size="small" variant="outlined" startIcon={<EventIcon fontSize="small" />} onClick={() => onBook(scheduling)}>Book: {scheduling}</Button>
        </Tooltip>
      )}
      <Dialog open={!!pending} onClose={() => !busy && setPending(null)} fullWidth maxWidth="xs">
        <DialogTitle>{pending ? ACTION_TEXT[pending.a.type] : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
          {pending?.a.type === 'done' && <Typography variant="body2">Mark the patient's open triage task as done?</Typography>}
          {pending?.a.type === 'followup' && (
            <TextField type="date" size="small" label="Follow-up date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} disabled={busy} />
          )}
          {pending && pending.a.type !== 'done' && (
            <TextField multiline minRows={3} size="small" label={pending.a.type === 'note' ? 'Note on the task' : 'Why (added as a note)'} value={text} onChange={(e) => setText(e.target.value)} disabled={busy} />
          )}
          {busy && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={busy || (pending?.a.type === 'note' && !text.trim()) || (pending?.a.type === 'followup' && !date)}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const PATIENT_PROMPTS = [
  'Draft a reply to their last message',
  "What's outstanding for this patient?",
  'Summarize the last 30 days',
  'Latest labs in plain English',
  'Current medications and allergies',
  'Upcoming and recent appointments',
];

export const CHAT_PROMPTS: { label: string; text: string }[] = [
  { label: 'Who is waiting longest?', text: 'Who has been waiting the longest for a reply? Give me the top 10 with what each one needs.' },
  { label: 'Huddle brief', text: "Give me today's huddle brief: today's appointments, urgent and clinical items waiting for a reply, and follow-ups due." },
  { label: 'Shift handoff', text: 'Write a shift handoff: what is still open, who is waiting on us, follow-ups due, and anything urgent, grouped by column.' },
  { label: 'Open clinical items', text: 'What is open in the Clinical column right now, and which ones need a reply?' },
];

export const QuickPrompts: React.FC<{ items: { label: string; text: string }[]; disabled?: boolean; onPick: (text: string) => void }> = ({ items, disabled, onPick }) => (
  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
    {items.map((p) => (
      <Chip key={p.label} size="small" variant="outlined" label={p.label} onClick={disabled ? undefined : () => onPick(p.text)} disabled={disabled} clickable={!disabled} />
    ))}
  </Box>
);
